import React from 'react'
import Select from 'react-select'
import {
  CARD_FIELD_OPTIONS,
  Opt,
  SOURCE_OPTIONS,
  SourceCode,
  sortOptionsForSources,
} from '../../../constants/carouselOptions'
import formStyles from '../../../styles/forms.module.scss'
import selectStyles from '../../../styles/react-select.module.scss'
import type { CarouselMetadata } from './CarouselEditor'
import CarouselFilterBuilder from './CarouselFilterBuilder'
import { descriptorsForSources } from './carouselFilterRegistry'
import styles from './CardCarouselEditor.module.scss'

interface CardCarouselEditorProps {
  metadata: CarouselMetadata
  onChange: (metadata: CarouselMetadata) => void
}

const CardCarouselEditor: React.FC<CardCarouselEditorProps> = ({ metadata, onChange }) => {
  const sources = (metadata.sources as SourceCode[]) ?? []

  // Apply a patch and drop any keys set to undefined, so the emitted config
  // never carries empty/stale fields.
  const apply = (patch: Partial<CarouselMetadata>) => {
    const next: CarouselMetadata = { ...metadata, ...patch }
    for (const k of Object.keys(next)) {
      if (next[k] === undefined) delete next[k]
    }
    onChange(next)
  }

  const handleSourcesChange = (next: SourceCode[]) => {
    // Drop filters that no longer apply to the new source set, and reconcile
    // the sort to one that's valid for it.
    const allowed = new Set(descriptorsForSources(next).map(d => d.key))
    const filters = Object.fromEntries(
      Object.entries(metadata.filters ?? {}).filter(([k]) => allowed.has(k)),
    )
    const sortOpts = sortOptionsForSources(next)
    const sort = sortOpts.some(o => o.value === metadata.sort) ? metadata.sort : sortOpts[0]?.value
    apply({
      sources: next.length ? next : undefined,
      filters: Object.keys(filters).length ? filters : undefined,
      sort,
      ended_grace_minutes: next.includes('matches') ? metadata.ended_grace_minutes : undefined,
    })
  }

  const sortOpts = sortOptionsForSources(sources)
  const showMoreEnabled = metadata.show_more != null

  return (
    <>
      <div className={formStyles.fieldGroup}>
        <label>Sources</label>
        <Select<Opt, true>
          isMulti
          options={SOURCE_OPTIONS}
          value={SOURCE_OPTIONS.filter(o => sources.includes(o.value as SourceCode))}
          onChange={(v) => handleSourcesChange(v.map(o => o.value as SourceCode))}
          className={selectStyles.reactSelect}
          classNamePrefix="react-select"
          placeholder="Select one or more sources…"
        />
      </div>

      {sources.length > 0 && (
        <>
          <div className={styles.section}>
            <CarouselFilterBuilder
              sources={sources}
              filters={metadata.filters ?? {}}
              onChange={(filters) => apply({ filters: Object.keys(filters).length ? filters : undefined })}
            />
          </div>

          <div className={styles.settingsGrid}>
            <div className={formStyles.fieldGroup}>
              <label>Sort</label>
              <Select<Opt, false>
                options={sortOpts}
                value={sortOpts.find(o => o.value === metadata.sort) ?? null}
                onChange={(o) => apply({ sort: o?.value })}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
                placeholder="Default order"
              />
            </div>
            <div className={formStyles.fieldGroup}>
              <label>Limit</label>
              <input
                type="number"
                min={1}
                max={100}
                placeholder="20"
                value={metadata.limit ?? ''}
                onChange={(e) => apply({ limit: e.target.value ? parseInt(e.target.value, 10) : undefined })}
              />
            </div>
            {sources.includes('matches') && (
              <div className={formStyles.fieldGroup}>
                <label title="How long a finished match lingers on a live rail before dropping off.">Ended grace (min)</label>
                <input
                  type="number"
                  min={0}
                  placeholder="e.g. 45"
                  title="How long a finished match lingers on a live rail before dropping off."
                  value={metadata.ended_grace_minutes ?? ''}
                  onChange={(e) => apply({ ended_grace_minutes: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                />
              </div>
            )}
          </div>

          <div className={formStyles.fieldGroup}>
            <label title="Which metadata renders on each card.">Card fields</label>
            <Select<Opt, true>
              isMulti
              options={CARD_FIELD_OPTIONS}
              value={CARD_FIELD_OPTIONS.filter(o => (metadata.card_fields ?? []).includes(o.value))}
              onChange={(v) => apply({ card_fields: v.length ? v.map(o => o.value) : undefined })}
              className={selectStyles.reactSelect}
              classNamePrefix="react-select"
              placeholder="Default set"
            />
          </div>

          <div className={formStyles.fieldGroup}>
            <label className={formStyles.checkboxLabel}>
              <input
                type="checkbox"
                checked={showMoreEnabled}
                onChange={(e) => apply({ show_more: e.target.checked ? { label: '', path: '' } : undefined })}
              />
              Add a “show more” link
            </label>
            {showMoreEnabled && (
              <div className={styles.grid2}>
                <input
                  type="text"
                  placeholder="Label — e.g. All Replays"
                  value={metadata.show_more?.label ?? ''}
                  onChange={(e) => apply({ show_more: { label: e.target.value, path: metadata.show_more?.path ?? '' } })}
                />
                <input
                  type="text"
                  placeholder="Path — e.g. /replays"
                  value={metadata.show_more?.path ?? ''}
                  onChange={(e) => apply({ show_more: { label: metadata.show_more?.label ?? '', path: e.target.value } })}
                />
              </div>
            )}
          </div>

          <details className={styles.jsonPreview}>
            <summary>Config preview</summary>
            <pre>{JSON.stringify(metadata, null, 2)}</pre>
          </details>
        </>
      )}
    </>
  )
}

export default CardCarouselEditor
